import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/auth";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import HeroReviewQuote from "@/components/HeroReviewQuote";
import { enrolInfo, enrolLine, fmtRange } from "@/lib/enrol";
import { SECTION_HEADINGS } from "@/admin/SectionHeadings";
import { crewDays } from "@/lib/enrol";
import { toast } from "sonner";
import { ArrowRight, Star, Trophy, Users, Sparkles, Medal, Send } from "lucide-react";
import { toInstaUrl } from "@/pages/Site";
import Reveal from "@/lib/Reveal";

const Section = ({ eyebrow, title, children, className = "", variant = "slide-up" }) => (
  <section className={`py-24 md:py-32 px-6 ${className}`}>
    <div className="max-w-[1400px] mx-auto">
      <Reveal variant={variant}>
        {eyebrow && <div className="eyebrow mb-4">{eyebrow}</div>}
        {title && <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.9] mb-12 uppercase">{title}</h2>}
      </Reveal>
      {children}
    </div>
  </section>
);

const HOME_CAT_STYLE = {
  team:      "border-red-500/40 text-red-400 bg-red-500/5",
  solo:      "border-sky-500/40 text-sky-400 bg-sky-500/5",
  duo:       "border-fuchsia-500/40 text-fuchsia-400 bg-fuchsia-500/5",
  trio:      "border-violet-500/40 text-violet-400 bg-violet-500/5",
  crew:      "border-orange-500/40 text-orange-400 bg-orange-500/5",
  school:    "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
  community: "border-amber-500/40 text-amber-400 bg-amber-500/5",
};

function homePlacementIcon(title = "") {
  const t = title.toLowerCase();
  if (t.includes("1st") || t.includes("gold") || t.includes("champion") || t.includes("winner")) return { Icon: Trophy, color: "text-yellow-400" };
  if (t.includes("2nd") || t.includes("silver") || t.includes("runner")) return { Icon: Medal, color: "text-zinc-300" };
  if (t.includes("3rd") || t.includes("bronze")) return { Icon: Medal, color: "text-amber-600" };
  return { Icon: Star, color: "text-red-500" };
}

function HomeAchievementsTable({ items, total }) {
  const rows = items
    .slice()
    .sort((a, b) => (String(b.date || "").localeCompare(String(a.date || ""))) || ((a.order ?? 0) - (b.order ?? 0)))
    .slice(0, 6);
  return (
    <div>
      <div className="card-surface overflow-x-auto" data-testid="home-achievements-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="text-left px-4 py-3 w-14"></th>
              <th className="text-left px-4 py-3 w-24">Year</th>
              <th className="text-left px-4 py-3 w-28">Category</th>
              <th className="text-left px-4 py-3">Placement</th>
              <th className="text-left px-4 py-3">Event / Competition</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(a => {
              const { Icon, color } = homePlacementIcon(a.title);
              const cat = (a.category || "").toLowerCase();
              const catCls = HOME_CAT_STYLE[cat] || "border-zinc-500/40 text-zinc-400 bg-zinc-500/5";
              return (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`home-ach-row-${a.id}`}>
                  <td className="px-4 py-3"><Icon size={18} className={color} /></td>
                  <td className="px-4 py-3 text-zinc-300 tabular-nums">{a.date || "—"}</td>
                  <td className="px-4 py-3">
                    {cat ? (
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 border ${catCls}`}>{cat}</span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3 font-display text-lg text-white">{a.title || "—"}</td>
                  <td className="px-4 py-3 text-zinc-200">{a.event || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {total > rows.length && (
        <div className="mt-4">
          <Link to="/achievements" className="btn-ghost !py-2 !px-4 !text-xs inline-flex items-center gap-1" data-testid="home-see-all-wins">
            See all {total} accolades <ArrowRight size={12}/>
          </Link>
        </div>
      )}
    </div>
  );
}

function HomeHireSection({ options }) {
  return (
    <Reveal variant="slide-up">
      <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="home-hire-section">
        {options.slice(0, 4).map(o => (
          <div key={o.id} className="card-surface p-6 flex flex-col" data-testid={`home-hire-card-${o.id}`}>
            <div className="eyebrow flex items-center gap-2 mb-2"><Sparkles size={12}/>Option</div>
            <h3 className="font-display text-xl uppercase mb-2 text-white">{o.name}</h3>
            <p className="text-zinc-400 text-sm line-clamp-3">{o.description || ""}</p>
          </div>
        ))}
      </div>
      <div className="text-center">
        <Link to="/hire" className="btn-red inline-flex items-center gap-2" data-testid="home-hire-cta">
          Hire us <ArrowRight size={16}/>
        </Link>
      </div>
    </Reveal>
  );
}

export default function Home() {
  const { settings } = useOutletContext();
  const [batches, setBatches] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reels, setReels] = useState([]);
  const [events, setEvents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [hireOptions, setHireOptions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [crews, setCrews] = useState([]);
  const [googleRating, setGoogleRating] = useState(null);

  // Mount-only: `api` is a module-level axios singleton; setters are stable. Content is static per page load.
  useEffect(() => {
    Promise.all([
      api.get("/batches"), api.get("/achievements"),
      api.get("/workshops"), api.get("/gallery"), api.get("/reels"),
      api.get("/events"), api.get("/sessions"), api.get("/hire-options"), api.get("/terms"), api.get("/crews"),
    ]).then(([b,a,w,g,rl,ev,se,ho,tm,cr]) => {
      setTerms(tm.data || []); setCrews((cr.data || []).filter(c => c.active !== false));
      setBatches(b.data); setAchievements(a.data);
      setWorkshops(w.data); setGallery(g.data); setReels(rl.data);
      setEvents((ev.data || []).filter(x => x.active !== false));
      setSessions(se.data || []);
      setHireOptions(ho.data || []);
    });
    api.get("/google-reviews").then(r => setGoogleRating(r.data)).catch(() => {});
  }, []);

  const enrolFor = (batchId) => enrolInfo(batchId, sessions, terms);
  const hd = (key) => {
    const d = SECTION_HEADINGS.find(x => x.key === key) || {};
    return { eyebrow: settings?.[`sec_${key}_eyebrow`] || d.eyebrow, title: settings?.[`sec_${key}_title`] || d.title };
  };
  const featured = (settings?.homepage_batches || [])
    .slice().sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(f => { const b = batches.find(x => x.id === f.batch_id); return b ? { ...f, name: b.name, image: f.image || b.image } : null; })
    .filter(Boolean);
  const sections = (settings?.homepage_sections || []).slice().sort((a,b) => (a.order||0) - (b.order||0));
  const isVisible = (key) => {
    const s = sections.find(x => x.key === key);
    return s ? !!s.visible : true;
  };
  const DEFAULT_ORDER = ["hero","stats","events","batches","competition","why","achievements","workshops","reviews","hire","gallery","reels","cta"];
  let orderedKeys = (sections.length ? sections.filter(s => s.visible).map(s => s.key) : DEFAULT_ORDER).filter(k => k !== "promotions");
  // Ensure new sections that aren't in settings.homepage_sections (e.g. "hire") still render.
  DEFAULT_ORDER.forEach(k => { if (!orderedKeys.includes(k) && !sections.find(s => s.key === k)) orderedKeys.push(k); });
  if (orderedKeys.includes("competition") && !sections.find(s => s.key === "competition")) {
    orderedKeys = orderedKeys.filter(k => k !== "competition");
    const bi = orderedKeys.indexOf("batches"); orderedKeys.splice(bi >= 0 ? bi + 1 : orderedKeys.length, 0, "competition");
  }

  const RENDERERS = {
    hero: () => (
      <section key="hero" className="relative h-[95vh] min-h-[640px] overflow-hidden grain">
        <img src={settings?.hero_image || "https://images.unsplash.com/photo-1550026593-cb89847b168d?w=1920"} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto h-full flex flex-col justify-end pb-24 px-6">
          <Reveal variant="slide-up">
            {settings?.hero_logo && (
              <div
                className={`mb-6 ${settings?.hero_logo_shape === "rectangle" ? "w-[340px] h-[180px] md:w-[440px] md:h-[220px]" : "w-[220px] h-[220px] md:w-[280px] md:h-[280px]"}`}
                data-testid="hero-logo-overlay"
                style={{
                  WebkitMaskImage: "radial-gradient(ellipse at center, #000 55%, transparent 90%)",
                  maskImage: "radial-gradient(ellipse at center, #000 55%, transparent 90%)",
                }}
              >
                <img src={settings.hero_logo} alt={settings.site_name || "2RF"} className="w-full h-full object-contain"/>
              </div>
            )}
            <div className="eyebrow mb-6" data-testid="hero-eyebrow" style={settings?.hero_eyebrow_color ? { color: settings.hero_eyebrow_color } : undefined}>{settings.hero_eyebrow || "Melbourne · Wyndham Vale & Point Cook"}</div>
            <h1 className="font-display text-[clamp(2.75rem,9vw,10rem)] leading-[0.9] tracking-tight uppercase max-w-5xl break-words" data-testid="hero-headline" style={settings?.hero_headline_color ? { color: settings.hero_headline_color } : undefined}>
              {settings?.hero_headline || "Find your rhythm."}
            </h1>
            <p className="text-zinc-300 text-lg md:text-xl max-w-2xl mt-6" data-testid="hero-sub" style={settings?.hero_sub_color ? { color: settings.hero_sub_color } : undefined}>{settings?.hero_sub || "Melbourne's growing Indian Hip hop Dance School for kids, teens and adults. Build confidence, fitness, friendships and stage presence through fun, high-energy classes."}</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/classes?trial=1" className="btn-red" data-testid="hero-cta-primary">Book your free trial <ArrowRight size={16}/></Link>
              <Link to="/timetable" className="btn-ghost" data-testid="hero-cta-secondary">View timetable</Link>
            </div>
            <HeroReviewQuote data={googleRating} />
          </Reveal>
        </div>
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 overflow-hidden py-4 bg-black/60 backdrop-blur">
          <div className="marquee font-display text-lg sm:text-2xl text-zinc-500">
            {Array(8).fill(settings?.marquee_text || "2RIGHTFEET · HIP HOP · MELBOURNE · SINCE 2021 ·").map((t,i) => <span key={`marquee-${i}`}>{t}</span>)}
          </div>
        </div>
      </section>
    ),
    stats: () => {
      const g = googleRating;
      const isGoogle = g?.enabled && g.rating;
      const reviewsStat = isGoogle
        ? { n: `${g.rating.toFixed(1)}★`, l: `Google (${g.user_ratings_total || 0})`, testid: "stat-google-rating" }
        : { n: settings?.stat_4_value || "5★", l: settings?.stat_4_label || "Parent reviews" };
      const reviewLink = g?.write_review_uri || settings?.google_business_url || g?.google_maps_uri;
      return (
      <div key="stats" className="border-y border-white/10 bg-black">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { n: settings?.stat_1_value || "100+", l: settings?.stat_1_label || "Happy students" },
            { n: settings?.stat_2_value || "5",    l: settings?.stat_2_label || "Years of teaching" },
            { n: settings?.stat_3_value || "2",    l: settings?.stat_3_label || "Current locations" },
            reviewsStat,
          ].map((s,i) => {
            const isReviewCell = i === 3 && !!reviewLink;
            return (
            <div key={s.testid || s.l} className="border-l border-red-600 pl-4" data-testid={s.testid}>
              <div className="font-display text-4xl md:text-5xl">{s.n}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{s.l}</div>
              {isReviewCell && (
                <a
                  href={reviewLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-[10px] uppercase tracking-widest text-red-500 hover:text-red-400 border-b border-red-500/40 hover:border-red-400 pb-0.5"
                  data-testid="home-google-review-link"
                >
                  Leave a Google review →
                </a>
              )}
            </div>
            );
          })}
        </div>
      </div>
      );
    },
    batches: () => (
      <Section key="batches" eyebrow={hd("batches").eyebrow} title={hd("batches").title} variant="slide-up">
        <Reveal variant="slide-up" stagger>
        {featured.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-batches">
          {featured.map((f, i) => (
            <Link key={f.batch_id} to={`/classes/${f.batch_id}?term=current`} className="group relative aspect-[4/3] sm:aspect-[4/5] overflow-hidden card-hover card-surface block" data-testid={`featured-batch-${i}`}>
              {f.image ? <img src={f.image} alt={f.name} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-90 transition-all duration-500" />
                : <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(220,38,38,0.35),transparent_60%)] bg-zinc-900" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              {enrolFor(f.batch_id) && (
                <span className="absolute top-4 left-4 z-10 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 shadow-lg shadow-red-900/40" data-testid={`featured-enrolling-${i}`}>Enrolling now</span>
              )}
              <div className="relative h-full flex flex-col justify-end p-6">
                <h3 className="font-display text-4xl uppercase leading-none">{f.name}</h3>
                {f.tagline && <p className="text-zinc-300 text-sm mt-3 line-clamp-2">{f.tagline}</p>}
                {enrolFor(f.batch_id) && <p className="text-red-400 text-[11px] uppercase tracking-widest mt-3" data-testid={`featured-term-${i}`}>{enrolLine(enrolFor(f.batch_id))}</p>}
                <span className="mt-4 text-red-500 text-xs uppercase tracking-widest font-bold flex items-center gap-1">Explore <ArrowRight size={12}/></span>
              </div>
            </Link>
          ))}
        </div>
        ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {batches.map((b, i) => {
            const info = enrolFor(b.id);
            return (
              <Link key={b.id} to={`/classes/${b.id}?term=current`} className="group relative aspect-[4/3] sm:aspect-[4/5] overflow-hidden card-hover card-surface block" data-testid={`batch-card-${i}`}>
                {b.image ? <img src={b.image} alt={b.name} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-90 transition-all duration-500" />
                  : <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(220,38,38,0.35),transparent_60%)] bg-zinc-900" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                {info && (
                  <span className="absolute top-4 left-4 z-10 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 shadow-lg shadow-red-900/40" data-testid={`batch-enrolling-${i}`}>Enrolling now</span>
                )}
                <div className="relative h-full flex flex-col justify-end p-6">
                  <div className="eyebrow text-white/70 mb-2">{b.age_range}</div>
                  <h3 className="font-display text-4xl uppercase leading-none">{b.name}</h3>
                  {b.description && <p className="text-zinc-300 text-sm mt-3 line-clamp-2">{b.description}</p>}
                  {info && <p className="text-red-400 text-[11px] uppercase tracking-widest mt-3" data-testid={`batch-term-${i}`}>{enrolLine(info)}</p>}
                  <span className="mt-4 text-red-500 text-xs uppercase tracking-widest font-bold flex items-center gap-1">Explore <ArrowRight size={12}/></span>
                </div>
              </Link>
            );
          })}
        </div>
        )}
        </Reveal>
      </Section>
    ),
    competition: () => {
      if (!crews.length) return null;
      return (
      <Section key="competition" eyebrow={hd("competition").eyebrow} title={hd("competition").title} variant="slide-left">
        <Reveal variant="slide-left" stagger>
        <div className={`grid gap-4 ${crews.length === 1 ? "md:grid-cols-1 max-w-3xl" : crews.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`} data-testid="competition-section">
          {crews.map((c) => {
            const img = c.group_photo || c.photos?.[0]?.url || c.photos?.[0] || "";
            return (
            <Link key={c.id} to={`/competition-team?apply=1&crew=${c.id}`} className="group relative aspect-[16/10] overflow-hidden card-hover card-surface block" data-testid={`competition-card-${c.id}`}>
              {img ? <img src={img} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 group-hover:opacity-80 transition-all duration-500" />
                : <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(220,38,38,0.4),transparent_60%)] bg-zinc-900" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <span className="absolute top-4 left-4 z-10 bg-black/60 backdrop-blur border border-white/10 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 flex items-center gap-1.5"><Trophy size={11} className="text-yellow-400"/> Audition only</span>
              <div className="relative h-full flex flex-col justify-end p-6">
                {c.age_range && <div className="eyebrow text-white/70 mb-2">{c.age_range}</div>}
                <h3 className="font-display text-4xl uppercase leading-none">{c.name}</h3>
                {crewDays(c) && <p className="text-red-400 text-[11px] uppercase tracking-widest mt-3" data-testid={`competition-schedule-${c.id}`}>{crewDays(c)}{c.start_time ? ` · ${fmtRange(c.start_time, c.end_time)}` : ""}{c.location_name ? ` · ${c.location_name}` : ""}</p>}
                <span className="mt-4 inline-flex w-fit items-center gap-2 bg-red-600 text-white text-xs uppercase tracking-widest font-bold px-4 py-2 group-hover:bg-red-500 transition-colors" data-testid={`competition-apply-${c.id}`}>Express interest <ArrowRight size={12}/></span>
              </div>
            </Link>
            );
          })}
        </div>
        <div className="mt-6"><Link to="/competition-team" className="text-red-500 text-xs uppercase tracking-widest font-bold flex items-center gap-1" data-testid="competition-more">Meet the crews <ArrowRight size={12}/></Link></div>
        </Reveal>
      </Section>
      );
    },
    events: () => events.length > 0 ? (
      <Section key="events" eyebrow={hd("events").eyebrow} title={hd("events").title} variant="slide-right">
        <Reveal variant="slide-right" stagger>
        <div className="grid md:grid-cols-2 gap-6">
          {events.slice(0, 4).map((e, i) => {
            const price = Number(e.price || 0);
            const label = e.link_label || e.external_label || (e.hosting === "external" ? "Go to event page" : price > 0 ? "Get tickets" : "RSVP · Learn more");
            return (
              <div key={e.id} className="card-surface overflow-hidden card-hover" data-testid={`event-card-${i}`}>
                {e.image && <img src={e.image} alt={e.caption || ""} className="w-full aspect-video object-cover" />}
                <div className="p-6">
                  {e.caption && <p className="text-zinc-200 text-base">{e.caption}</p>}
                  {(e.date || e.location) && (
                    <div className="text-zinc-500 text-xs mt-2">{e.date}{e.date && e.location ? " · " : ""}{e.location}</div>
                  )}
                  <div className="mt-4 flex items-center gap-4">
                    {e.hosting === "external" && e.external_url ? (
                      <a href={e.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-red-500 text-xs uppercase tracking-widest font-bold" data-testid={`event-cta-${i}`}>
                        {label} <ArrowRight size={12}/>
                      </a>
                    ) : (
                    <Link to={`/events/${e.id}`} className="inline-flex items-center gap-2 text-red-500 text-xs uppercase tracking-widest font-bold" data-testid={`event-cta-${i}`}>
                      {label} <ArrowRight size={12}/>
                    </Link>
                    )}
                    {price > 0 && <span className="text-xs text-zinc-500 uppercase tracking-widest">${price} {e.currency || "AUD"}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </Reveal>
      </Section>
    ) : null,
    why: () => {
      const rawFeatures = (settings?.why_features || "").trim();
      const DEFAULT_FEATURES = [
        { i: Sparkles, t: "Build confidence", d: "Shy beginners blossom into expressive performers." },
        { i: Users, t: "Real friendships", d: "Local kids and families who become a second crew." },
        { i: Trophy, t: "Competition pathway", d: "Optional teams for dancers ready to level up." },
        { i: Sparkles, t: "Energy & fitness", d: "Active, fun cardio your child looks forward to." },
        { i: Users, t: "Safe environment", d: "Family-friendly culture led by professional coaches." },
        { i: Trophy, t: "Real stage time", d: "Showcases, school events and community performances." },
      ];
      const iconRotation = [Sparkles, Users, Trophy, Sparkles, Users, Trophy];
      const parsed = rawFeatures
        ? rawFeatures.split("\n").map(line => line.trim()).filter(Boolean).map((line, i) => {
            const [t, ...rest] = line.split("|");
            return { i: iconRotation[i % 6], t: (t || "").trim(), d: rest.join("|").trim() };
          })
        : DEFAULT_FEATURES;
      const eyebrow = settings?.sec_why_eyebrow || settings?.why_eyebrow || "Why parents choose us";
      const title = settings?.sec_why_title || settings?.why_title || "More than dance. A confidence engine.";
      const eyebrowStyle = settings?.why_eyebrow_color ? { color: settings.why_eyebrow_color } : undefined;
      const titleStyle = settings?.why_title_color ? { color: settings.why_title_color } : undefined;
      return (
        <section key="why" className="py-24 md:py-32 px-6 bg-[#0A0A0A]">
          <div className="max-w-[1400px] mx-auto">
            <Reveal variant="fade">
              <div className="eyebrow mb-4" style={eyebrowStyle} data-testid="why-eyebrow">{eyebrow}</div>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.9] mb-12 uppercase" style={titleStyle} data-testid="why-title">{title}</h2>
            </Reveal>
            <Reveal variant="slide-up" stagger>
              <div className="grid md:grid-cols-3 gap-4" data-testid="why-features">
                {parsed.map((f, i) => {
                  const Icon = f.i;
                  return (
                    <div key={f.t || i} className="card-surface p-8 card-hover">
                      <Icon className="text-red-500 mb-4" size={28} />
                      <h4 className="font-display text-2xl uppercase mb-2">{f.t}</h4>
                      <p className="text-zinc-400 text-sm">{f.d}</p>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </section>
      );
    },
    achievements: () => achievements.length > 0 ? (
      <Section key="achievements" eyebrow={hd("achievements").eyebrow} title={hd("achievements").title} variant="tilt">
        <Reveal variant="tilt">
        <HomeAchievementsTable items={achievements} total={achievements.length} />
        </Reveal>
      </Section>
    ) : null,
    workshops: () => workshops.length > 0 ? (
      <Section key="workshops" eyebrow={hd("workshops").eyebrow} title={hd("workshops").title} className="bg-[#0A0A0A]" variant="slide-left">
        <Reveal variant="slide-left" stagger>
        <div className="grid md:grid-cols-2 gap-4">
          {workshops.slice(0,2).map(w => (
            <Link to="/workshops" key={w.id} className="card-surface card-hover flex flex-col md:flex-row overflow-hidden">
              {w.image && <img src={w.image} className="md:w-1/2 aspect-video md:aspect-auto object-cover" alt="" />}
              <div className="p-6 flex-1">
                <div className="eyebrow mb-2">{w.date}</div>
                <h3 className="font-display text-2xl uppercase">{w.name}</h3>
                <p className="text-zinc-400 text-sm mt-2 line-clamp-3">{w.description}</p>
                <div className="mt-4 text-red-500 text-sm font-bold">${w.price} · {w.location_name}</div>
              </div>
            </Link>
          ))}
        </div>
        </Reveal>
      </Section>
    ) : null,
    reviews: () => {
      const g = googleRating;
      if (!g?.enabled || !Array.isArray(g?.reviews) || g.reviews.length === 0) return null;
      const writeUrl = g?.write_review_uri || settings?.google_business_url || g?.google_maps_uri;
      return (
      <Section key="reviews" eyebrow={hd("reviews").eyebrow} title={hd("reviews").title} variant="fade">
        <Reveal variant="fade">
          <GoogleReviewsCarousel data={g} testPrefix="home-google-review" reviewUrl={writeUrl} />
        </Reveal>
        {writeUrl && (
          <div className="text-center mt-8">
            <a href={writeUrl} target="_blank" rel="noreferrer" className="btn-red" data-testid="home-reviews-google-cta">Leave a Google review →</a>
          </div>
        )}
      </Section>
      );
    },
    hire: () => hireOptions.length > 0 ? (
      <Section key="hire" eyebrow={hd("hire").eyebrow} title={hd("hire").title} className="bg-[#0A0A0A]" variant="slide-up">
        <HomeHireSection options={hireOptions} />
      </Section>
    ) : null,
    gallery: () => gallery.length > 0 ? (
      <Section key="gallery" eyebrow={hd("gallery").eyebrow} title={hd("gallery").title} variant="mask">
        <Reveal variant="mask" stagger>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {gallery.slice(0,8).map(g => (
            <div key={g.id} className="aspect-square overflow-hidden">
              <img src={g.image} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
            </div>
          ))}
        </div>
        </Reveal>
        <div className="text-center mt-8"><Link to="/gallery" className="btn-ghost">View gallery</Link></div>
      </Section>
    ) : null,
    reels: () => reels.length > 0 ? (
      <Section key="reels" eyebrow={settings?.sec_reels_eyebrow || settings?.reels_handle || settings?.instagram_handle || "Follow along"} title={settings?.sec_reels_title || settings?.reels_title || "Latest reels."} className="bg-[#0A0A0A]" variant="slide-right">
        <Reveal variant="slide-right" stagger>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reels.slice(0, 6).map(r => (
            <div key={r.id} className="card-surface overflow-hidden" data-testid={`reel-${r.id}`}>
              <div className="aspect-[9/12] bg-black">
                <iframe src={r.embed_url} title="Instagram reel" className="w-full h-full" frameBorder="0" allowFullScreen scrolling="no" allow="encrypted-media"/>
              </div>
              {r.caption && <div className="p-4 text-sm text-zinc-400">{r.caption}</div>}
            </div>
          ))}
        </div>
        </Reveal>
        <div className="text-center mt-8">
          {settings?.instagram && (
            <a href={toInstaUrl(settings.instagram)} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="follow-instagram-cta">Follow on Instagram</a>
          )}
        </div>
      </Section>
    ) : null,
    cta: () => (
      <section key="cta" className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-black opacity-20" />
        <div className="relative max-w-[1400px] mx-auto text-center">
          <Reveal variant="zoom">
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.85]">Spots fill<br/>every term.</h2>
            <p className="text-zinc-300 mt-6 max-w-xl mx-auto">Help your child build confidence, make friends and stay active while learning exciting Hip Hop routines.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <Link to="/classes?trial=1" className="btn-red" data-testid="final-cta-book">Book your free trial</Link>
              <Link to="/contact" className="btn-ghost">Contact us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    ),
  };

  return <>{orderedKeys.map(k => RENDERERS[k] ? RENDERERS[k]() : null)}</>;
}
